/**
 * Phase 3a-5 — OCI (Organizational Conflict of Interest) safe-lane workspace.
 * Reached at /oci/:projectId within the dashboard layout.
 *
 * Surfaces the raise → route → decide → mitigate flow over zou's OCI RPCs.
 * Opening a mitigation writes the authz deny overlay (3a-4) that makes the
 * restricted artifacts resolve to NoView for the restricted users — the
 * masking itself is enforced at those artifact read surfaces, not here.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorState } from '../../components/layout';
import { OCI_FUNCTIONAL_OWNERS, useDecideOciFlag, useOciFlags, useOpenOciMitigation, useRaiseOciFlag, useRouteOciFlag } from './ociHooks';
const SIGNAL_KINDS = [
    'graph_relationship',
    'prior_work',
    'vehicle_overlap',
    'personnel',
    'manual'
];
export default function OciWorkspacePage() {
    const { projectId } = useParams();
    if (!projectId) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6 text-red-600",
            children: "Missing projectId in the URL."
        });
    }
    return /*#__PURE__*/ _jsx(OciWorkspace, {
        projectId: projectId
    });
}
function OciWorkspace({ projectId }) {
    const flagsQuery = useOciFlags(projectId);
    const raise = useRaiseOciFlag();
    const [title, setTitle] = useState('');
    const [signalKind, setSignalKind] = useState(SIGNAL_KINDS[0]);
    const flags = flagsQuery.data ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "p-6 space-y-6",
        "data-testid": Testids.oci.panelRoot,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: "text-xl font-semibold",
                        children: "OCI Safe Lane"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Raise conflict-of-interest flags, route them to a functional owner, decide, and open a mitigation (firewall plan) that walls off restricted users from restricted artifacts."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "rounded border border-gray-200 p-4 space-y-3",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-medium",
                        children: "Raise a flag"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-sm",
                                children: [
                                    "Title",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.oci.raiseTitleInput,
                                        className: "border rounded px-2 py-1",
                                        value: title,
                                        onChange: (e)=>setTitle(e.target.value),
                                        placeholder: "Incumbent subcontractor overlap"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-sm",
                                children: [
                                    "Signal",
                                    /*#__PURE__*/ _jsx("select", {
                                        "data-testid": Testids.oci.raiseSignalSelect,
                                        className: "border rounded px-2 py-1",
                                        value: signalKind,
                                        onChange: (e)=>setSignalKind(e.target.value),
                                        children: SIGNAL_KINDS.map((s)=>/*#__PURE__*/ _jsx("option", {
                                                value: s,
                                                children: s
                                            }, s))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.oci.raiseSubmit,
                                className: "rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50",
                                disabled: !title.trim() || raise.isPending,
                                onClick: ()=>{
                                    raise.mutate({
                                        projectId,
                                        signalKind,
                                        title: title.trim()
                                    }, {
                                        onSuccess: ()=>setTitle('')
                                    });
                                },
                                children: "Raise flag"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("h2", {
                        className: "font-medium",
                        children: [
                            "Flags (",
                            flags.length,
                            ")"
                        ]
                    }),
                    flagsQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Loading…"
                    }),
                    flagsQuery.isError && /*#__PURE__*/ _jsx(ErrorState, {
                        title: "Couldn't load OCI flags",
                        message: "We couldn't load the OCI flags for this project. This is usually temporary — try again.",
                        onRetry: ()=>void flagsQuery.refetch()
                    }),
                    !flagsQuery.isLoading && !flagsQuery.isError && flags.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "No OCI flags raised on this project yet."
                    }),
                    flags.map((flag)=>/*#__PURE__*/ _jsx(OciFlagRow, {
                            flag: flag
                        }, flag.id))
                ]
            })
        ]
    });
}
function OciFlagRow({ flag }) {
    const route = useRouteOciFlag();
    const decide = useDecideOciFlag();
    const openMitigation = useOpenOciMitigation();
    const [owner, setOwner] = useState(flag.functionalOwner || OCI_FUNCTIONAL_OWNERS[0]);
    const [reason, setReason] = useState('');
    const [firewall, setFirewall] = useState('');
    const [users, setUsers] = useState('');
    const [artifacts, setArtifacts] = useState('');
    const terminal = flag.status === 'cleared' || flag.status === 'waived';
    const splitCsv = (s)=>s.split(',').map((v)=>v.trim()).filter(Boolean);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.oci.flagRow,
        className: "rounded border border-gray-200 p-4 space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: flag.title
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "ml-2 text-xs text-gray-500",
                                children: flag.signalKind
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.oci.flagStatus,
                        className: "rounded bg-gray-100 px-2 py-0.5 text-xs font-medium",
                        children: flag.status
                    })
                ]
            }),
            !terminal && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Route to",
                                    /*#__PURE__*/ _jsx("select", {
                                        "data-testid": Testids.oci.routeOwnerSelect,
                                        className: "border rounded px-2 py-1",
                                        value: owner,
                                        onChange: (e)=>setOwner(e.target.value),
                                        children: OCI_FUNCTIONAL_OWNERS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                                value: o,
                                                children: o
                                            }, o))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.oci.routeSubmit,
                                className: "rounded border px-3 py-1 disabled:opacity-50",
                                disabled: route.isPending,
                                onClick: ()=>route.mutate({
                                        ociFlagId: flag.id,
                                        functionalOwner: owner
                                    }),
                                children: "Route"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Decision reason",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.oci.decideReasonInput,
                                        className: "border rounded px-2 py-1",
                                        value: reason,
                                        onChange: (e)=>setReason(e.target.value),
                                        placeholder: "documented rationale"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.oci.decideClearButton,
                                className: "rounded border border-green-600 px-3 py-1 text-green-700 disabled:opacity-50",
                                disabled: !reason.trim() || decide.isPending,
                                onClick: ()=>decide.mutate({
                                        ociFlagId: flag.id,
                                        decision: 'cleared',
                                        reason: reason.trim()
                                    }),
                                children: "Clear"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.oci.decideWaiveButton,
                                className: "rounded border border-amber-600 px-3 py-1 text-amber-700 disabled:opacity-50",
                                disabled: !reason.trim() || decide.isPending,
                                onClick: ()=>decide.mutate({
                                        ociFlagId: flag.id,
                                        decision: 'waived',
                                        reason: reason.trim()
                                    }),
                                children: "Waive"
                            })
                        ]
                    })
                ]
            }),
            !terminal && /*#__PURE__*/ _jsxs("div", {
                className: "rounded bg-gray-50 p-3 space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs font-medium text-gray-600",
                        children: "Open mitigation (firewall)"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Firewall plan",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.oci.mitigationFirewallInput,
                                        className: "border rounded px-2 py-1",
                                        value: firewall,
                                        onChange: (e)=>setFirewall(e.target.value),
                                        placeholder: "wall off the incumbent team"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Restricted user ids",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.oci.mitigationUsersInput,
                                        className: "border rounded px-2 py-1",
                                        value: users,
                                        onChange: (e)=>setUsers(e.target.value),
                                        placeholder: "user-1, user-2"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex flex-col text-xs",
                                children: [
                                    "Restricted artifact ids",
                                    /*#__PURE__*/ _jsx("input", {
                                        "data-testid": Testids.oci.mitigationArtifactsInput,
                                        className: "border rounded px-2 py-1",
                                        value: artifacts,
                                        onChange: (e)=>setArtifacts(e.target.value),
                                        placeholder: "artifact-1, artifact-2"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": Testids.oci.mitigationSubmit,
                                className: "rounded bg-red-600 px-3 py-1 text-white disabled:opacity-50",
                                disabled: openMitigation.isPending,
                                onClick: ()=>openMitigation.mutate({
                                        ociFlagId: flag.id,
                                        firewallPlan: firewall.trim(),
                                        restrictedUserIds: splitCsv(users),
                                        restrictedArtifactIds: splitCsv(artifacts)
                                    }),
                                children: "Open mitigation"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
